if (!window.__psrScriptLoaded) {
  window.__psrScriptLoaded = true;

  window.__productCache = window.__productCache || new Map();
  const cache = window.__productCache;

  if (!window.__psr_injectedSrcs) window.__psr_injectedSrcs = new Set();
  let _clickDebounceTimer = null;

  // ---------------------------------------------------------------------------
  // Request layer: prefetch concurrency limiter + 429-aware retry.
  // ---------------------------------------------------------------------------
  const PREFETCH_MAX_CONCURRENT = 3;
  const MAX_RETRIES = 3;
  const BASE_BACKOFF_MS = 800;

  const prefetchQueue = [];
  let prefetchActive = 0;

  function schedulePrefetch(task) {
    return new Promise((resolve, reject) => {
      prefetchQueue.push({ task, resolve, reject });
      drainPrefetchQueue();
    });
  }

  function drainPrefetchQueue() {
    while (prefetchActive < PREFETCH_MAX_CONCURRENT && prefetchQueue.length > 0) {
      const { task, resolve, reject } = prefetchQueue.shift();
      prefetchActive += 1;
      task()
        .then(resolve, reject)
        .finally(() => {
          prefetchActive -= 1;
          drainPrefetchQueue();
        });
    }
  }

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Fetches JSON and retries on 429, respecting Retry-After or backing off.
  async function fetchJsonWithRetry(fetchUrl) {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
      const response = await fetch(fetchUrl);

      if (response.ok) {
        // Shopify answers with the full HTML page when the sections param is not
        // recognised; fail with a clear error instead of a JSON SyntaxError.
        const contentType = response.headers.get('Content-Type') || '';
        if (!contentType.includes('application/json')) {
          throw new Error(`Expected JSON but got "${contentType}" for ${fetchUrl}`);
        }
        return response.json();
      }

      if (response.status === 429 && attempt < MAX_RETRIES) {
        const retryAfter = parseFloat(response.headers.get('Retry-After'));
        const waitMs = Number.isFinite(retryAfter)
          ? retryAfter * 1000
          : BASE_BACKOFF_MS * Math.pow(2, attempt) + Math.random() * 300;
        await sleep(waitMs);
        continue;
      }

      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    throw new Error(`Exhausted retries for ${fetchUrl}`);
  }

  // ---------------------------------------------------------------------------
  // Intent-based prefetch: warm a single swatch's sections only when the user
  // hovers / focuses / touches it. Nothing is fetched on page load, so there is
  // no request burst to trip Cloudflare rate limiting.
  // ---------------------------------------------------------------------------
  const HOVER_PREFETCH_DELAY = 120;
  const prefetchInFlight = new Set();
  let _hoverPrefetchTimer = null;

  function prefetchUrl(url) {
    if (!url || cache.has(url) || prefetchInFlight.has(url)) return;

    const sections = getSectionsToRender(5);
    const fetchUrl = buildFetchUrl(url, sections);

    prefetchInFlight.add(url);
    schedulePrefetch(() => fetchJsonWithRetry(fetchUrl))
      .then(data => cache.set(url, data))
      .catch(error => console.error(`Error prefetching ${url}:`, error))
      .finally(() => prefetchInFlight.delete(url));
  }

  function onIntentPrefetch(event) {
    const anchor = event.target.closest('.product-variant-picker-color a[href]');
    if (!anchor) return;
    const url = normalizeProductUrl(anchor.getAttribute('href'));
    clearTimeout(_hoverPrefetchTimer);
    _hoverPrefetchTimer = setTimeout(() => prefetchUrl(url), HOVER_PREFETCH_DELAY);
  }

  function cancelIntentPrefetch() {
    clearTimeout(_hoverPrefetchTimer);
  }

  function attachIntentPrefetchListeners() {
    // Delegated so it also covers swatches injected after render.
    document.addEventListener('pointerover', onIntentPrefetch);
    document.addEventListener('focusin', onIntentPrefetch);
    document.addEventListener('touchstart', onIntentPrefetch, { passive: true });
    document.addEventListener('pointerout', cancelIntentPrefetch);
  }

  // ---------------------------------------------------------------------------

  function attachProductClickListener() {
    const products = document.querySelector(".product-variant-picker-color");
    if (!products) return;

    products.removeEventListener("click", handleClick);
    products.addEventListener("click", handleClick);
    earlyAccess();
  }

  function getSectionsToRender(limit = 6) {
    const sectionsWrapper = document.getElementById('MainContent');
    if (!sectionsWrapper) return [];

    return [...sectionsWrapper.querySelectorAll('.shopify-section')]
      .slice(0, limit)
      .map(section => ({
        id: section.id,
        section: section.id.replace('shopify-section-', ''),
        selector: '#' + section.id
      }));

    // Always include dynamic-title regardless of position
    const dynamicTitle = allSections.find(s => s.section.endsWith('__dynamic-title') || s.section === 'dynamic-title');
    const otherSections = allSections.filter(s => s !== dynamicTitle).slice(0, limit);

    return dynamicTitle ? [...otherSections, dynamicTitle] : otherSections;
  }

  // Tracking params appended to links by third-party scripts (e.g. Google's
  // cross-domain linker adds `_gl=...`, `_gl=--blocked` without consent).
  const TRACKING_PARAMS = ['_gl'];

  // Returns a same-origin path + query without tracking params, so it is safe to
  // use as cache key, fetch base and history URL.
  function normalizeProductUrl(href) {
    if (!href) return href;
    const parsed = new URL(href, window.location.origin);
    TRACKING_PARAMS.forEach(param => parsed.searchParams.delete(param));
    return `${parsed.pathname}${parsed.search}`;
  }

  function buildFetchUrl(url, sections) {
    // Use URLSearchParams: the href may already carry a query string, and
    // appending `?sections=` blindly produced `?_gl=--blocked?sections=...`,
    // which Shopify ignores and answers with the full HTML page.
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete('sections');
    const separator = parsed.search ? '&' : '?';
    const sectionParams = sections.map(s => s.section).join(',');
    return `${parsed.pathname}${parsed.search}${separator}sections=${sectionParams}`;
  }

  function getSectionInnerHTML(html, selector) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const element = parsedDocument.querySelector(selector);
    return element ? element.innerHTML.replace(/<img([^>]*)>/g, '<img$1 loading="lazy">') : '';
  }

  function executeScriptsFromHTML(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const scripts = doc.querySelectorAll("script");

    scripts.forEach(script => {
      if (script.src) {
        if (window.__psr_injectedSrcs.has(script.src)) return;
        window.__psr_injectedSrcs.add(script.src);
        const newScript = document.createElement("script");
        newScript.src = script.src;
        newScript.async = true;
        document.body.appendChild(newScript);
      } else if (script.textContent.trim()) {
        const scriptContent = script.textContent.trim();
        if (!scriptContent.startsWith('{') && !scriptContent.startsWith('[')) {
          try {
            const inlineScript = document.createElement("script");
            inlineScript.textContent = scriptContent;
            document.body.appendChild(inlineScript);
          } catch (error) {
            console.error("Error executing inline script:", error, scriptContent);
          }
        }
      }
    });
  }

  function reloadScriptsFromDomain(domain) {
    const scriptsToReload = [...document.querySelectorAll(`script[src^="${domain}"]`)]
      .map(script => script.src)
      .filter(src => !src.includes('unnecessary'));

    scriptsToReload.forEach(src => {
      document.querySelector(`script[src="${src}"]`)?.remove();
    });

    scriptsToReload.forEach(src => {
      const newScript = document.createElement("script");
      newScript.src = src;
      newScript.async = true;
      newScript.onload = () => {
        //console.log(`Reloaded script: ${src}`);
      };
      document.body.appendChild(newScript);
    });
  }

  function handleResponse(request, sections, newUrl) {
    const res = JSON.parse(request.responseText);

    sections.forEach(section => {
      const elementToReplace =
        document.getElementById(section.id)?.querySelector(section.selector) ||
        document.getElementById(section.id);

      if (elementToReplace) {
        const innerHTML = getSectionInnerHTML(res[section.section], section.selector);
        elementToReplace.innerHTML = innerHTML;

        executeScriptsFromHTML(res[section.section]);
      }
    });

    const activeImage = document.querySelector("media-gallery .product__media-item.is-active .product__media img");
    if (activeImage && activeImage.src) {
      const popupImage = document.querySelector(".product-popup-modal.email-me-popup .left-product-image img");
      if (popupImage) {
        popupImage.src = activeImage.src;
        popupImage.srcset = activeImage.src;
      }
    }

    if (window.location.href !== newUrl) {
      window.history.replaceState(null, '', newUrl);
    }

    const meta = extractMetaFromResponse(res);
    updateHeadForNewUrl(newUrl, meta);

    attachProductClickListener();

    if (window.requestIdleCallback) {
      requestIdleCallback(() => { window.reinitBundleBtn?.(); });
    } else {
      setTimeout(() => { window.reinitBundleBtn?.(); }, 100);
    }
  }

  function extractTitleFromResponse(res) {
    const titleKey = Object.keys(res).find(k => k.endsWith('__dynamic-title') || k === 'dynamic-title');
    if (!titleKey) return null;

    const html = res[titleKey];
    if (!html) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('[data-dynamic-title]')?.textContent?.trim() || null;
  }

  function extractMetaFromResponse(res) {
    // Find dynamic-title section key (could be 'dynamic-title' or 'template--XXX__dynamic-title')
    const titleKey = Object.keys(res).find(k => k.endsWith('dynamic-title') || k === 'dynamic-title');
    if (!titleKey || !res[titleKey]) return { title: null, image: null };

    const doc = new DOMParser().parseFromString(res[titleKey], 'text/html');

    const titleEl = doc.querySelector('[data-dynamic-title]');
    const imageEl = doc.querySelector('[data-dynamic-og-image]');

    return {
      title: titleEl?.textContent?.trim() || null,
      image: imageEl ? {
        url: imageEl.dataset.imageUrl,
        width: imageEl.dataset.imageWidth,
        height: imageEl.dataset.imageHeight,
        alt: imageEl.dataset.imageAlt,
      } : null,
    };
  }

  function updateHeadForNewUrl(newUrl, meta) {
    const absoluteUrl = new URL(newUrl, window.location.origin);
    const cleanUrl = `${absoluteUrl.origin}${absoluteUrl.pathname}`;

    // 1. Title
    if (meta?.title) {
      document.title = meta.title;
    }

    // 2. Canonical
    document.querySelectorAll('link[rel="canonical"]').forEach(el => el.remove());
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = cleanUrl;
    document.head.appendChild(canonical);

    // 3. og:url og:title
    document.querySelectorAll('meta[property="og:url"]').forEach(el => el.remove());
    const ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    ogUrl.setAttribute('content', cleanUrl);
    document.head.appendChild(ogUrl);

    document.querySelectorAll('meta[property="og:title"]').forEach(el => el.remove());
    const ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.setAttribute('content', meta.title);
    document.head.appendChild(ogTitle);

    // 4. og:image (+ secure_url, width, height)
    if (meta?.image?.url) {
      document.querySelectorAll(
        'meta[property="og:image"], meta[property="og:image:secure_url"], meta[property="og:image:width"], meta[property="og:image:height"], meta[property="og:image:alt"]'
      ).forEach(el => el.remove());

      const ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      ogImage.setAttribute('content', meta.image.url);
      document.head.appendChild(ogImage);

      const ogImageSecure = document.createElement('meta');
      ogImageSecure.setAttribute('property', 'og:image:secure_url');
      ogImageSecure.setAttribute('content', meta.image.url);
      document.head.appendChild(ogImageSecure);

      if (meta.image.width) {
        const ogImageW = document.createElement('meta');
        ogImageW.setAttribute('property', 'og:image:width');
        ogImageW.setAttribute('content', meta.image.width);
        document.head.appendChild(ogImageW);
      }
      if (meta.image.height) {
        const ogImageH = document.createElement('meta');
        ogImageH.setAttribute('property', 'og:image:height');
        ogImageH.setAttribute('content', meta.image.height);
        document.head.appendChild(ogImageH);
      }
      if (meta.image.alt) {
        const ogImageAlt = document.createElement('meta');
        ogImageAlt.setAttribute('property', 'og:image:alt');
        ogImageAlt.setAttribute('content', meta.image.alt);
        document.head.appendChild(ogImageAlt);
      }
    }

    // 5. twitter:url twitter:title
    document.querySelectorAll('meta[name="twitter:url"]').forEach(el => el.remove());
    const twitterUrl = document.createElement('meta');
    twitterUrl.setAttribute('name', 'twitter:url');
    twitterUrl.setAttribute('content', cleanUrl);
    document.head.appendChild(twitterUrl);

    document.querySelectorAll('meta[name="twitter:title"]').forEach(el => el.remove());
    const twitterTitle = document.createElement('meta');
    twitterTitle.setAttribute('name', 'twitter:title');
    twitterTitle.setAttribute('content', meta.title);
    document.head.appendChild(twitterTitle);

    // 6. twitter:image (recommend keep in sync with og:image)
    if (meta?.image?.url) {
      document.querySelectorAll('meta[name="twitter:image"]').forEach(el => el.remove());
      const twitterImage = document.createElement('meta');
      twitterImage.setAttribute('name', 'twitter:image');
      twitterImage.setAttribute('content', meta.image.url);
      document.head.appendChild(twitterImage);
    }
  }

  function fetchSections(url, sections) {
    const fetchUrl = buildFetchUrl(url, sections);

    // Click-initiated fetch: goes straight through (not queued behind prefetch)
    // so a user's color click stays responsive. Still retries on 429.
    return fetchJsonWithRetry(fetchUrl)
      .then(data => {
        cache.set(url, data);
        return { responseText: JSON.stringify(data) };
      })
      .then(response => handleResponse(response, sections, url))
      .catch(error => {
        console.error(`Error fetching sections: ${error}`);
        // Fall back to a normal page load so the color switch still happens.
        window.location.href = url;
      });
  }

  function handleClick(event) {
    const anchor = event.target.closest("a[href]");
    if (!anchor) return;
    event.preventDefault();

    const url = normalizeProductUrl(anchor.getAttribute("href"));
    const productID = anchor.getAttribute("data-product-id");
    const productCat = event.target.closest("[data-product-cat]")?.getAttribute("data-product-cat");
    const customerEmail = event.target.closest("[data-customer-email]")?.getAttribute("data-customer-email");

    clearTimeout(_clickDebounceTimer);
    _clickDebounceTimer = setTimeout(() => {
      if (!window._scarabEventTriggered) {
        window._scarabEventTriggered = true;
        window.ScarabQueue = window.ScarabQueue || [];
        ScarabQueue.push(['view', `${productID}`]);
        ScarabQueue.push(['category', `${productCat}`]);
        ScarabQueue.push(['displayCurrency', window.Shopify.currency.active]);
        if (customerEmail !== '') {
          ScarabQueue.push(['setEmail', `${customerEmail}`]);
        }
        ScarabQueue.push(['cart', window.cartItems || []]);
        ScarabQueue.push(['go']);
        setTimeout(() => { window._scarabEventTriggered = false; }, 100);
      }

      document.querySelectorAll("product-modal").forEach(el => el.remove());

      const sections = getSectionsToRender(5);
      if (cache.has(url)) {
        const cachedData = cache.get(url);
        handleResponse({ responseText: JSON.stringify(cachedData) }, sections, url);
      } else {
        Promise.all([fetchSections(url, sections)]);
      }
    }, 300);
  }

  document.addEventListener('click', (e) => {
    const stickyBtn = e.target.closest('.sticky-add-to-cart');
    if (!stickyBtn) return;

    e.preventDefault();

    const mainBtn = document.querySelector('.product-main-atc.product-form__submit');
    if (mainBtn) {
      mainBtn.click();
    }
  });


  document.addEventListener("DOMContentLoaded", () => {
    attachProductClickListener();
    // No eager prefetch on load. Sections are warmed on hover/focus/touch intent,
    // which removes the load-time request burst that triggered 429.
    attachIntentPrefetchListeners();
  });
}